import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import useApi from "../hook/useApi";
import { useAuth } from "../context/AuthContext";
import Loader from "./Loader/Loader";
import { formatCurrency, formatDateFull } from "../utils/helper";

const Row = ({ label, children }) => (
  <div className="col-md-6 mb-16">
    <p className="text-secondary-light text-sm mb-4">{label}</p>
    <div className="text-md fw-medium mb-0">{children}</div>
  </div>
);

const SegmentBlock = ({ title, seg }) => (
  <div className="border radius-8 p-20 mb-20 bg-base h-100">
    <h6 className="fw-semibold mb-16 text-primary-600">{title}</h6>

    {!seg || !seg.enabled ? (
      <span className="text-secondary-light">Disabled</span>
    ) : (
      <div className="row g-3">
        <Row label="Margin Limit">
          {formatCurrency(seg.marginLimit)}
        </Row>

        <Row label="Max User Margin">
          {formatCurrency(seg.maxUserMarginLimit)}
        </Row>

        <Row label="Brokerage Type">
          {seg.brokerageType || "N/A"}
        </Row>

        <Row label="Delivery Brokerage">
          {seg.brokerageType === "flat" && "₹"}{seg.deliveryBrokerage}{seg.brokerageType === "percentage" && "%"}
        </Row>

        <Row label="Intraday Brokerage">
          {seg.brokerageType === "flat" && "₹"}{seg.intradayBrokerage}{seg.brokerageType === "percentage" && "%"}
        </Row>

        <Row label="Enabled">
          {seg.enabled ? "Yes" : "No"}
        </Row>
      </div>
    )}
  </div>
);

const ViewSubadminLayer = () => {

  const navigate = useNavigate();
  const { subAdminId, profile } = useAuth();
  const { AsyncGetApiCall } = useApi();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const { id } = useParams();
  const userId = subAdminId ? profile?._id : id;

  const fetchSubadmin = async () => {
    setLoading(true);

    const result = await AsyncGetApiCall(
      `/subadmin/get-subadmin/${userId}`,
      {},
    );

    if (result?.errors) {
      toast.error(
        Array.isArray(result.errors)
          ? result.errors[0]
          : result.errors,
      );

      setDetail(null);
    } else if (result?.success && result?.data) {
      setDetail(result.data);
    } else {
      toast.error(result?.message || "Subadmin not found");
      setDetail(null);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (userId) {
      fetchSubadmin();
    }
  }, [userId]);

  if (loading) return <Loader />;

  if (!detail && !subAdminId) {
    return (
      <div className="card p-24 text-center">
        <h5 className="mb-16">Subadmin not found</h5>

        <button
          type="button"
          className="btn btn-primary"
          onClick={() => navigate("/subadmin-list")}
        >
          Back to list
        </button>
      </div>
    );
  }

  return (
    <div className="card h-100 p-0 radius-12">
      {!subAdminId &&
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex flex-wrap justify-content-between align-items-center gap-2">
          <h5 className="mb-0">
            Subadmin Details: {detail.masterName} ({detail.loginId})
          </h5>

          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => navigate("/subadmin-list")}
            >
              Back
            </button>

            {!subAdminId && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() =>
                  navigate(`/subadmin-update/${detail._id}`, {
                    state: { subadmin: detail },
                  })
                }
              >
                Edit
              </button>
            )}
          </div>
        </div>
      }

      <div className="card-body p-24">
        {/* Basic Information */}
        <div className="row border-bottom mb-24 pb-8">
          <div className="col-12">
            <h6 className="text-primary-600 mb-16">
              Basic Information
            </h6>
          </div>

          <Row label="Login ID">{detail.loginId}</Row>

          <Row label="Name">
            {detail.masterName || "—"}
          </Row>

          <Row label="Current Balance">
            {formatCurrency(detail.currentBalance)}
          </Row>

          {(detail?.unsettledPnl !== 0 && detail?.unsettledPnl !== undefined) && (
            <Row label="Unsettled P&L">
              <span className={detail?.unsettledPnl >= 0 ? 'text-success' : 'text-danger'}>
                {formatCurrency(detail.unsettledPnl)}
              </span>
            </Row>
          )}

          <Row label="Partnership">
            {detail.partnership}%
          </Row>

          <Row label="Only Position">
            {detail.onlyPosition ? "Yes" : "No"}
          </Row>

          <Row label="Limit Orders">
            {detail.limitOrders ? "Yes" : "No"}
          </Row>

          <Row label="Status">
            {detail.isActive ? (
              <span className="text-success">Active</span>
            ) : (
              <span className="text-danger">Inactive</span>
            )}
          </Row>

          <Row label="Created At">
            {formatDateFull(detail.createdAt)}
          </Row>

          <div className="col-12">
            <Row label="Remarks">
              {detail.remarks || "—"}
            </Row>
          </div>
        </div>

        {/* Segment Settings */}
        <div className="row">
          {detail.segmentSettings &&
            Object.keys(detail.segmentSettings).length > 0 ? (
            Object.entries(detail.segmentSettings).map(
              ([key, seg]) => (
                <div className="col-md-6" key={key}>
                  <SegmentBlock
                    title={`${key.toUpperCase()} Settings`}
                    seg={seg}
                  />
                </div>
              ),
            )
          ) : (
            <div className="col-12 text-center text-secondary-light">
              No segment settings available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewSubadminLayer;